// APO (MageWorx Advanced Product Options) v2 adapter — a SECOND SOURCE
// for the same LineItemProperty[] shape readLineItemProperties already
// produces from `properties[...]` form inputs (product-button.ts).
//
// Why this exists: APO v2 renders NO `properties[...]` form inputs at
// all — it clones the theme's Add-to-Cart button and injects the
// shopper's selections at add-to-cart time via its own JS. So today a
// quote request from an APO v2 store captures neither the chosen
// options nor their prices. APO v1 stores DO use real form inputs
// (`window.mwProductOptionsObjects`, no `window.mwApo`) — those are
// already fully handled by readLineItemProperties and this adapter
// deliberately returns [] for them (see the mwApo check below).
//
// Verified v2 public API (live forensics, majesticfountains.com,
// 2026-07-28):
//   window.mwApo = { version, ready, getInstance, instances, on, off }
//   getInstance() → { productId, variantId, getState(), getOptions(),
//                      getValues(optionId), getSelections(), on, off, … }
//   getState()    → { productId, variantId, price,
//                      options: [{ id, title, type, required, hidden,
//                                  valueIds }] }
//   getSelections() → selected value ids, e.g. ["v_19664905"]
//   getValues(optionId) → that option's value objects (shape only
//     PARTIALLY observed — treated defensively below: expect an `id` +
//     a title-ish field + a price-ish field, tolerate anything else).
//
// Robustness contract (this runs inside the click path on storefronts
// we don't control): EVERY call into `mwApo`/its instance is wrapped so
// a throw, a hostile return value, or a future MageWorx API change
// degrades to "no options captured" (today's behavior) — NEVER a
// broken quote button. See src/embed/apo-adapter.test.ts's hostility
// matrix.

import type { LineItemProperty } from "./product-button";

/** Total cap on synthesized properties — belt and suspenders alongside
 *  the server's own MAX_LINE_ITEM_PROPERTIES clamp. */
const MAX_PROPERTIES = 20;
/** Per-field length clamp — the server clamps again (500 chars) before
 *  storage; this just keeps a hostile/huge value from ballooning the
 *  in-memory payload before it gets there. */
const MAX_NAME_LEN = 100;
const MAX_VALUE_LEN = 200;

function trimStr(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

function clamp(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) : s;
}

/** Call a possibly-absent/hostile function, swallowing any throw and
 *  treating a null/undefined return the same as "nothing". Never lets
 *  a 3rd-party function's misbehavior escape into the click path. */
function tryCall<T>(fn: unknown, args: unknown[], fallback: T): T {
  if (typeof fn !== "function") return fallback;
  try {
    const r = (fn as (...a: unknown[]) => T).apply(undefined, args);
    return r === undefined || r === null ? fallback : r;
  } catch {
    return fallback;
  }
}

/** Parse a price-ish field defensively. Accepts a plain number, or a
 *  string like "62.00" / "62.00__" (APO stamps cart-line surcharges as
 *  "62.00__" — strip anything that isn't a digit or a decimal point and
 *  parse what's left). Anything unparseable → 0 (treated as "no
 *  price" by the caller — a label without a price still beats nothing). */
function parsePriceLike(raw: unknown): number {
  if (raw == null) return 0;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : 0;
  if (typeof raw === "string") {
    const cleaned = raw.replace(/[^0-9.]/g, "");
    if (!cleaned) return 0;
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/** Renders the priced-option suffix this adapter stamps onto a value
 *  string when a selection carries a price (e.g. "Fatwood (+$62.00)").
 *  Exported so src/lib/request-option-import.ts's parser can be
 *  cross-tested against the exact string this emits — the parse side
 *  must stay in lockstep with this format or request-import silently
 *  drops priced options into notes instead of pricing them. */
export function formatPriceSuffix(price: number): string {
  return ` (+$${price.toFixed(2)})`;
}

export interface ParsedPriceSuffix {
  /** The value substring before the suffix, e.g. "Fatwood" out of
   *  "Fatwood (+$62.00)". */
  label: string;
  /** Parsed dollar amount, always > 0. */
  price: number;
}

// Matches a trailing price suffix at the END of the value string only —
// a suffix that isn't trailing (embedded mid-value) is not a match in
// any of these grammars. `<amount>` tolerates thousands commas
// ("1,026.00") defensively, even though formatPriceSuffix itself never
// emits them (toFixed(2) doesn't), in case a value was hand-assembled
// or came from a differently-formatted source. Each pattern requires an
// explicit delimiter before the amount — a value merely *containing* a
// dollar figure (e.g. "$569.00" alone, or "$569 option applies…" mid-
// string) must never match; see the false-positive matrix in
// apo-adapter.test.ts.
const PRICE_SUFFIX_PATTERNS: RegExp[] = [
  // Ours: " (+$X)" / " (+ $X)" — byte-compatible with the original
  // single-pattern regex (zero-space case still matches identically).
  / \(\+\s?\$([0-9][0-9,]*\.\d{2})\)$/,
  // Ymq ("Ymq Product Options"): " | $X" / " | +$X" — pipe-delimited
  // tail, e.g. "12 GPM UV Water Sterilizer | $569.00".
  / \| \+?\$([0-9][0-9,]*\.\d{2})$/,
  // Bare plus-delimited tail, no parens/pipe: " + $X" / " +$X", e.g.
  // "Brass Bullets +$195.00".
  / \+ ?\$([0-9][0-9,]*\.\d{2})$/,
];

/** Parses a value string's trailing price suffix, if present and
 *  well-formed under any of the recognized grammars. THE shared parser
 *  — request-option-import.ts (rep import) and src/embed/item-price.ts
 *  (drawer display math) both import this rather than each keeping
 *  their own regex, so the two sides of the round-trip (a customer's
 *  configured price shown in the drawer vs. the same price
 *  reconstructed as parent + option line items on the rep's imported
 *  quote) can never silently drift apart on what counts as "priced."
 *  Returns null (→ treat as unpriced) when no grammar matches, the
 *  matched suffix isn't at the end, the amount doesn't parse, or the
 *  amount is <= 0 (e.g. "(+$0.00)" — a priced label with no actual
 *  price). Currency is `$`-only across every grammar; a non-`$` tail
 *  never matches. */
export function parsePriceSuffix(value: string): ParsedPriceSuffix | null {
  for (const re of PRICE_SUFFIX_PATTERNS) {
    const m = re.exec(value);
    if (!m) continue;
    const amount = parseFloat(m[1].replace(/,/g, ""));
    if (!Number.isFinite(amount) || amount <= 0) return null;
    return { label: value.slice(0, m.index), price: amount };
  }
  return null;
}

/** Probe the common price-ish fields on a value object. Field name is
 *  only partially observed on the live store, so this checks several
 *  plausible shapes and takes the first positive parse. */
function extractPrice(valObj: any): number {
  if (!valObj || typeof valObj !== "object") return 0;
  try {
    const candidates = [valObj.price, valObj.value?.price, valObj.additionalPrice, valObj.priceAdjustment];
    for (const c of candidates) {
      const n = parsePriceLike(c);
      if (n > 0) return n;
    }
  } catch {
    /* hostile getter — treat as no price */
  }
  return 0;
}

/** Detect APO v2 (`window.mwApo` with a callable `getInstance`). Returns
 *  false for APO v1 stores (`window.mwProductOptionsObjects`, no
 *  `mwApo`) and for stores with no APO at all — both cases already
 *  produce [] from readApoSelections, this just documents the v1
 *  carve-out explicitly per the kickoff's instruction. */
function hasApoV2(win: any): boolean {
  try {
    return !!(win && win.mwApo && typeof win.mwApo.getInstance === "function");
  } catch {
    return false;
  }
}

/** Read the shopper's current APO v2 selections (+ best-effort prices)
 *  as a LineItemProperty[] — the same shape readLineItemProperties
 *  produces from real form inputs, so callers can merge the two without
 *  caring which source a given property came from.
 *
 *  `doc` is accepted (mirrors readLineItemProperties's DOM-facing
 *  signature / leaves room for a future DOM-based fallback) but unused
 *  today — every read goes through the mwApo instance API. */
export function readApoSelections(win: any, _doc?: any): LineItemProperty[] {
  try {
    if (!hasApoV2(win)) return [];
    // v1 stores (window.mwProductOptionsObjects, no window.mwApo) never
    // reach here — the `hasApoV2` check above already excluded them, and
    // readLineItemProperties' existing `properties[...]` form-input scan
    // is what captures their selections.

    const instance = tryCall<any>(win.mwApo.getInstance, [], null);
    if (!instance || typeof instance !== "object") return [];

    // Options: prefer the dedicated getOptions(), fall back to
    // getState().options — both shapes were observed live.
    const optionsFromMethod = tryCall<any>(instance.getOptions, [], null);
    const state = tryCall<any>(instance.getState, [], null);
    const stateOptions = state && Array.isArray(state.options) ? state.options : null;
    const options: any[] = Array.isArray(optionsFromMethod) && optionsFromMethod.length
      ? optionsFromMethod
      : (stateOptions || []);
    if (!Array.isArray(options) || options.length === 0) return [];

    const rawSelections = tryCall<any>(instance.getSelections, [], null);
    if (!Array.isArray(rawSelections) || rawSelections.length === 0) return [];
    const selectedIds = new Set<string>();
    for (const s of rawSelections) {
      try {
        selectedIds.add(String(s));
      } catch {
        /* skip an unstringifiable entry */
      }
    }
    if (selectedIds.size === 0) return [];

    const out: LineItemProperty[] = [];
    const nameCounts = new Map<string, number>();

    for (const option of options) {
      if (out.length >= MAX_PROPERTIES) break;
      if (!option || typeof option !== "object") continue;
      if (option.hidden === true) continue; // merchant hid this option — don't surface it

      const valueIds: any[] = Array.isArray(option.valueIds) ? option.valueIds : [];
      const matchedValueIds = valueIds.filter(vid => {
        try {
          return selectedIds.has(String(vid));
        } catch {
          return false;
        }
      });
      if (matchedValueIds.length === 0) continue;

      const optionTitle = clamp(trimStr(option.title) || "Option", MAX_NAME_LEN);

      const values = tryCall<any>(instance.getValues, [option.id], null);
      const valuesById = new Map<string, any>();
      if (Array.isArray(values)) {
        for (const v of values) {
          try {
            if (v && v.id != null) valuesById.set(String(v.id), v);
          } catch {
            /* skip a hostile value entry */
          }
        }
      }

      for (const vid of matchedValueIds) {
        if (out.length >= MAX_PROPERTIES) break;
        let valueTitle = "";
        let price = 0;
        try {
          const valObj = valuesById.get(String(vid));
          valueTitle = trimStr(valObj?.title) || trimStr(valObj?.name) || String(vid);
          price = extractPrice(valObj);
        } catch {
          valueTitle = String(vid);
          price = 0;
        }
        const displayValue = price > 0 ? `${valueTitle}${formatPriceSuffix(price)}` : valueTitle;

        // Multiple selections within one option (checkbox groups) each
        // get their OWN property, with the option title de-duped
        // ("Accessories", "Accessories (2)", …) rather than joined into
        // one value — keeps each selection's price visible on its own
        // line instead of buried in a combined string.
        const count = (nameCounts.get(optionTitle) || 0) + 1;
        nameCounts.set(optionTitle, count);
        const name = count === 1 ? optionTitle : `${optionTitle} (${count})`;

        out.push({ name: clamp(name, MAX_NAME_LEN), value: clamp(displayValue, MAX_VALUE_LEN) });
      }
    }
    return out;
  } catch {
    // Any unexpected throw anywhere above (hostile getter, changed API
    // shape, …) — never break the quote button. No options captured is
    // exactly today's behavior.
    return [];
  }
}
